/**
 * Gleason Score Fields
 *
 * This component is used to display the Gleason Score fields.
 * It is used in the various prostate biopsy and prostate post-rp forms.
 *
 * @param control - The control object from react-hook-form
 * @param errors - The errors object from react-hook-form
 * @param trigger - The trigger function from react-hook-form
 * @param onPrimaryChange - The function to call when the primary Gleason score changes
 * @param onSecondaryChange - The function to call when the secondary Gleason score changes
 * @returns The Gleason Score fields component
 */
import { Controller, Path, UseFormTrigger } from 'react-hook-form';
import { Box } from '@mui/material';
import z from 'zod';
import { RadioGroupField } from 'src/components/radio_group';
import {
  FormFieldProps,
  GLEASON_SCORE_OPTIONS,
} from 'src/distribution_platforms/features/order_form';

export const REQUIRED_GLEASON_SCORE_FIELDS_SCHEMA = z.object({
  primaryGleasonScore: z.enum(['3', '4', '5'], {
    error: 'Primary Gleason Score is required',
  }),
  secondaryGleasonScore: z.enum(['3', '4', '5'], {
    error: 'Secondary Gleason Score is required',
  }),
});

export const OPTIONAL_GLEASON_SCORE_SCHEMA = z.enum(['3', '4', '5']).optional();

export const OPTIONAL_GLEASON_SCORE_FIELDS_SCHEMA = z.object({
  primaryGleasonScore: OPTIONAL_GLEASON_SCORE_SCHEMA,
  secondaryGleasonScore: OPTIONAL_GLEASON_SCORE_SCHEMA,
});

export type GleasonScoreFieldsValues = z.infer<
  | typeof REQUIRED_GLEASON_SCORE_FIELDS_SCHEMA
  | typeof OPTIONAL_GLEASON_SCORE_FIELDS_SCHEMA
>;

export const GleasonScoreFields = <T extends GleasonScoreFieldsValues>({
  control,
  errors,
  trigger,
  onPrimaryChange,
  onSecondaryChange,
}: FormFieldProps<T> & {
  trigger?: UseFormTrigger<T>;
  /** Called after primary Gleason score change (e.g. to re-validate dependent fields). */
  onPrimaryChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => void | Promise<void>;
  /** Called after secondary Gleason score change (e.g. to re-validate dependent fields). */
  onSecondaryChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => void | Promise<void>;
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      gap: { xs: 2, sm: 3 },
    }}
  >
    <Box
      sx={{
        flex: 1,
      }}
    >
      <Controller
        name={'primaryGleasonScore' as Path<T>}
        control={control}
        render={({ field }) => (
          <RadioGroupField
            row
            required
            name={field.name}
            label='Primary Gleason Score'
            value={field.value}
            onBlur={field.onBlur}
            options={GLEASON_SCORE_OPTIONS}
            validationText={
              errors.primaryGleasonScore?.message?.toString() ?? ''
            }
            onChange={async (event) => {
              field.onChange(event);
              // Manually trigger validation to clear errors
              if (event.target.value) {
                await trigger?.('primaryGleasonScore' as Path<T>);
              }
              await onPrimaryChange?.(event);
            }}
          />
        )}
      />
    </Box>
    <Box
      sx={{
        flex: 1,
      }}
    >
      <Controller
        name={'secondaryGleasonScore' as Path<T>}
        control={control}
        render={({ field }) => (
          <RadioGroupField
            row
            required
            name={field.name}
            label='Secondary Gleason Score'
            value={field.value}
            onBlur={field.onBlur}
            options={GLEASON_SCORE_OPTIONS}
            validationText={
              errors.secondaryGleasonScore?.message?.toString() ?? ''
            }
            onChange={async (event) => {
              field.onChange(event);
              // Manually trigger validation to clear errors
              if (event.target.value) {
                await trigger?.('secondaryGleasonScore' as Path<T>);
              }
              await onSecondaryChange?.(event);
            }}
          />
        )}
      />
    </Box>
  </Box>
);
